import { useState, useEffect, useRef, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Loader2, MapPin, Users, Globe, Calendar, Clock, AlertTriangle, Radio, Search, ExternalLink, Flag } from "lucide-react";
import { MapContainer, TileLayer, CircleMarker, Popup, useMap, GeoJSON } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import { format, formatDistanceToNow } from "date-fns";
import { 
  findCountry, 
  getCountryCoordinates, 
  getCountryKeys, 
  getNormalizedCountryName,
  normalizeCountryKey 
} from "@/utils/countryData";

interface Company {
  id: string;
  name: string;
}

interface ActiveEmployee {
  id: string;
  requestId: string;
  employeeId: string;
  employeeName: string;
  origin: string;
  destination: string;
  startDate: string;
  endDate: string;
  riskLevel: string;
  latitude?: number;
  longitude?: number;
  isOnline?: boolean;
}

interface CountryRisk {
  name: string;
  riskLevel: string;
  latitude?: number;
  longitude?: number;
}

interface CountryDetails {
  name: string;
  employees: ActiveEmployee[];
  riskLevel: string;
  latitude: number;
  longitude: number;
}

interface SOSAlert {
  id: string;
  country_name: string;
  alert_type: string;
  severity: string;
  title: string;
  description: string;
  source: string;
  created_at: string;
  is_active: boolean;
}

interface WorldMapTabProps {
  company: Company | null;
}

type RiskLevel = "low" | "medium" | "high";

// Component to handle map view changes
const MapController = ({ center, zoom }: { center: [number, number]; zoom: number }) => {
  const map = useMap();
  useEffect(() => {
    map.setView(center, zoom);
  }, [center, zoom, map]);
  return null;
};

const WorldMapTab = ({ company }: WorldMapTabProps) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [activeEmployees, setActiveEmployees] = useState<ActiveEmployee[]>([]);
  const [countryRisks, setCountryRisks] = useState<Map<string, CountryRisk>>(new Map());
  const [worldGeoJson, setWorldGeoJson] = useState<any>(null);
  const [sosAlerts, setSOSAlerts] = useState<SOSAlert[]>([]);
  const [selectedCountry, setSelectedCountry] = useState<CountryDetails | null>(null);
  const [onlineUsers, setOnlineUsers] = useState<Set<string>>(new Set());
  const [mapCenter, setMapCenter] = useState<[number, number]>([30, 0]);
  const [mapZoom, setMapZoom] = useState(2);
  const [alertFilter, setAlertFilter] = useState<'all' | 'active'>('all');
  const [alertSearch, setAlertSearch] = useState('');
  const channelRef = useRef<ReturnType<typeof supabase.channel> | null>(null);

  // Setup realtime presence for online status
  useEffect(() => {
    if (!company?.id) return;

    const channel = supabase.channel(`company-${company.id}-presence`);
    
    channel
      .on('presence', { event: 'sync' }, () => {
        const state = channel.presenceState();
        const onlineIds = new Set<string>();
        Object.values(state).flat().forEach((presence: any) => {
          if (presence.employee_id) {
            onlineIds.add(presence.employee_id);
          }
        });
        setOnlineUsers(onlineIds);
      })
      .subscribe();

    channelRef.current = channel;

    return () => {
      channel.unsubscribe();
    };
  }, [company?.id]);

  useEffect(() => {
    if (company?.id) {
      fetchData();
    }
  }, [company?.id]);

  useEffect(() => {
    // Lightweight country polygons for coloring by risk level (no DB lat/long needed)
    fetch("/world.geojson")
      .then((r) => r.json())
      .then(setWorldGeoJson)
      .catch((e) => console.error("Error loading world.geojson:", e));
  }, []);

  const fetchData = async () => {
    try {
      await Promise.all([
        fetchActiveEmployees(),
        fetchCountryRisks(),
        fetchSOSAlerts(),
      ]);
    } finally {
      setLoading(false);
    }
  };

  const fetchActiveEmployees = async () => {
    try {
      const today = new Date().toISOString().split('T')[0];
      
      const { data, error } = await supabase
        .from('remote_work_requests')
        .select(`
          id,
          destination_country,
          start_date,
          end_date,
          risk_level,
          employees!inner(
            id,
            first_name,
            last_name,
            residence_country
          )
        `)
        .eq('company_id', company?.id)
        .eq('hr_approval_status', 'approved')
        .eq('manager_approval_status', 'approved')
        .gte('end_date', today);

      if (error) throw error;

      // Use countryData utility for reliable coordinates (fallback from DB)
      const { data: dbCountries } = await supabase
        .from('countries')
        .select('name, latitude, longitude');

      const dbCountryCoords = new Map(
        dbCountries?.map(c => [c.name, { lat: c.latitude, lng: c.longitude }]) || []
      );

      const employees: ActiveEmployee[] = (data || []).map((req: any) => {
        const destination = req.destination_country || 'Unknown';
        
        // Try DB coords first, then fallback to countryData utility
        let coords = dbCountryCoords.get(destination);
        if (!coords?.lat || !coords?.lng) {
          const utilityCoords = getCountryCoordinates(destination);
          if (utilityCoords) {
            coords = { lat: utilityCoords.latitude, lng: utilityCoords.longitude };
          }
        }

        return {
          id: req.id,
          requestId: req.id?.slice(0, 8).toUpperCase() || 'N/A',
          employeeId: req.employees?.id || '',
          employeeName: `${req.employees?.first_name || ''} ${req.employees?.last_name || ''}`.trim(),
          origin: req.employees?.residence_country || 'Unknown',
          destination,
          startDate: req.start_date,
          endDate: req.end_date,
          riskLevel: req.risk_level || 'low',
          latitude: coords?.lat ?? undefined,
          longitude: coords?.lng ?? undefined,
        };
      });

      setActiveEmployees(employees);
    } catch (error) {
      console.error('Error fetching active employees:', error);
    }
  };

  const fetchCountryRisks = async () => {
    try {
      // Fetch global risk levels from countries table
      const { data: countriesData, error: countriesError } = await supabase
        .from('countries')
        .select('name, risk_level, latitude, longitude');

      if (countriesError) throw countriesError;

      // Fetch company-specific risk overrides from company_countries
      const { data: companyCountriesData, error: ccError } = await supabase
        .from('company_countries')
        .select('country_id, risk_level_override, countries!inner(name)')
        .eq('company_id', company?.id)
        .not('risk_level_override', 'is', null);

      // Build override map by normalized country key
      const overrideMap = new Map<string, RiskLevel>();
      if (!ccError && companyCountriesData) {
        companyCountriesData.forEach((cc: any) => {
          const dbName = cc.countries?.name as string | undefined;
          const override = cc.risk_level_override as RiskLevel | undefined;
          if (!dbName || !override) return;
          getCountryKeys(dbName).forEach((k) => overrideMap.set(k, override));
        });
      }

      const risksMap = new Map<string, CountryRisk>();
      (countriesData || []).forEach((country) => {
        const keys = getCountryKeys(country.name);
        const override = keys.map((k) => overrideMap.get(k)).find(Boolean);
        const effectiveRiskLevel: RiskLevel = (override || country.risk_level || "low") as RiskLevel;

        keys.forEach((k) => {
          risksMap.set(k, {
            name: country.name,
            riskLevel: effectiveRiskLevel,
            latitude: country.latitude ?? undefined,
            longitude: country.longitude ?? undefined,
          });
        });
      });

      setCountryRisks(risksMap);
    } catch (error) {
      console.error('Error fetching country risks:', error);
    }
  };

  const fetchSOSAlerts = async () => {
    try {
      const { data, error } = await supabase
        .from('sos_alerts')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(50);

      if (error) throw error;
      setSOSAlerts(data || []);
    } catch (error) {
      console.error('Error fetching SOS alerts:', error);
    }
  };

  // Group employees by destination
  const employeesByDestination = useMemo(() => {
    return activeEmployees.reduce((acc, emp) => {
      if (!acc[emp.destination]) {
        acc[emp.destination] = [];
      }
      acc[emp.destination].push({
        ...emp,
        isOnline: onlineUsers.has(emp.employeeId)
      });
      return acc;
    }, {} as Record<string, ActiveEmployee[]>);
  }, [activeEmployees, onlineUsers]);

  const employeesByCountryKey = useMemo(() => {
    const map = new Map<string, ActiveEmployee[]>();
    Object.entries(employeesByDestination).forEach(([dest, employees]) => {
      getCountryKeys(dest).forEach((k) => map.set(k, employees));
    });
    return map;
  }, [employeesByDestination]);

  const getRiskLevelForCountry = (countryName: string): RiskLevel => {
    const keys = getCountryKeys(countryName);
    for (const k of keys) {
      const risk = countryRisks.get(k)?.riskLevel as RiskLevel | undefined;
      if (risk) return risk;
    }
    return "low";
  };

  // Helper to get full country risk data (returns null if country not in our data)
  const getCountryRiskData = (countryName: string): CountryRisk | null => {
    const keys = getCountryKeys(countryName);
    for (const k of keys) {
      const data = countryRisks.get(k);
      if (data) return data;
    }
    return null;
  };

  // Get all countries with risk levels for markers (even without employees)
  const allCountryMarkers = useMemo(() => {
    const markers: CountryDetails[] = [];
    
    // Add countries with employees
    Object.entries(employeesByDestination).forEach(([destination, employees]) => {
      const firstEmployee = employees[0];
      
      // Try to get coordinates from employee, or fallback to countryData utility
      let lat = firstEmployee.latitude;
      let lng = firstEmployee.longitude;
      
      if (!lat || !lng) {
        const coords = getCountryCoordinates(destination);
        if (coords) {
          lat = coords.latitude;
          lng = coords.longitude;
        }
      }
      
      // Skip if still no coordinates
      if (!lat || !lng) return;

      const riskLevel = getRiskLevelForCountry(destination);

      markers.push({
        name: destination,
        employees,
        riskLevel,
        latitude: lat,
        longitude: lng,
      });
    });
    
    return markers;
  }, [employeesByDestination, countryRisks]);

  const getRiskColor = (level: string) => {
    switch (level) {
      case 'high':
        return 'hsl(var(--destructive))';
      case 'medium':
        return 'hsl(var(--warning))';
      default:
        return 'hsl(var(--success))';
    }
  };

  const getRiskBadgeClass = (level: string) => {
    switch (level) {
      case 'high':
        return 'bg-destructive/10 text-destructive border-destructive/20';
      case 'medium':
        return 'bg-warning-light text-warning border-warning/20';
      default:
        return 'bg-success-light text-success border-success/20';
    }
  };

  const getSeverityBadgeClass = (severity: string) => {
    switch (severity) {
      case 'critical':
        return 'bg-destructive text-destructive-foreground';
      case 'high':
        return 'bg-destructive/10 text-destructive';
      case 'medium':
        return 'bg-warning-light text-warning';
      default:
        return 'bg-muted text-muted-foreground';
    }
  };

  const getAlertTypeIcon = (type: string) => {
    switch (type) {
      case 'security':
        return '🔒';
      case 'health':
        return '🏥';
      case 'weather':
        return '🌪️';
      case 'political':
        return '🏛️';
      default:
        return '⚠️';
    }
  };

  const handleCountryClick = (country: CountryDetails) => {
    setSelectedCountry(country);
    setMapCenter([country.latitude, country.longitude]);
    setMapZoom(5);
  };

  const handleCloseDetails = () => {
    setSelectedCountry(null);
    setMapCenter([30, 0]);
    setMapZoom(2);
  };

  const filteredAlerts = useMemo(() => {
    let filtered = sosAlerts;
    
    if (alertFilter === 'active') {
      filtered = filtered.filter(a => a.is_active);
    }
    
    if (alertSearch) {
      const search = alertSearch.toLowerCase();
      filtered = filtered.filter(a => 
        a.country_name.toLowerCase().includes(search) ||
        a.title.toLowerCase().includes(search)
      );
    }
    
    return filtered;
  }, [sosAlerts, alertFilter, alertSearch]);

  const onlineCount = activeEmployees.filter(e => onlineUsers.has(e.employeeId)).length;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <Globe className="h-6 w-6 text-primary" />
            {t("dashboard.worldMap.title", "SOS Center")}
          </h2>
          <p className="text-muted-foreground">
            {t("dashboard.worldMap.description", "Current employees traveling")}
          </p>
        </div>
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2 px-3 py-1.5 bg-muted rounded-full">
            <Users className="h-4 w-4 text-muted-foreground" />
            <span className="font-semibold">{activeEmployees.length}</span>
            <span className="text-muted-foreground text-sm">traveling</span>
          </div>
          {onlineCount > 0 && (
            <div className="flex items-center gap-2 px-3 py-1.5 bg-success-light rounded-full border border-success/20">
              <Radio className="h-4 w-4 text-success animate-pulse" />
              <span className="font-semibold text-success">{onlineCount}</span>
              <span className="text-success text-sm">online</span>
            </div>
          )}
        </div>
      </div>

      {/* Map */}
      <Card className="overflow-hidden">
        <CardContent className="p-0 relative">
          <MapContainer
            center={mapCenter}
            zoom={mapZoom}
            style={{ height: '450px', width: '100%' }}
            scrollWheelZoom={true}
            className="z-0"
          >
            <MapController center={mapCenter} zoom={mapZoom} />
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />

            {/* Country polygons colored by risk level */}
            {worldGeoJson && (
              <GeoJSON
                key={JSON.stringify(Array.from(countryRisks.keys()))}
                data={worldGeoJson}
                style={(feature: any) => {
                  const name = feature?.properties?.name as string | undefined;
                  if (!name) {
                    return {
                      fillColor: '#e5e7eb',
                      fillOpacity: 0.3,
                      color: 'hsl(var(--border))',
                      weight: 1,
                      opacity: 0.5,
                    };
                  }

                  const countryData = getCountryRiskData(name);
                  
                  // If country has no explicit risk level set, show neutral gray
                  if (!countryData) {
                    return {
                      fillColor: '#e5e7eb',
                      fillOpacity: 0.3,
                      color: 'hsl(var(--border))',
                      weight: 1,
                      opacity: 0.5,
                    };
                  }

                  // Country has risk data - show with appropriate color
                  const riskColors: Record<RiskLevel, string> = {
                    low: '#22c55e',     // Green
                    medium: '#f59e0b',  // Orange/Amber
                    high: '#ef4444',    // Red
                  };

                  return {
                    fillColor: riskColors[countryData.riskLevel as RiskLevel] || '#22c55e',
                    fillOpacity: 0.45,
                    color: riskColors[countryData.riskLevel as RiskLevel] || '#22c55e',
                    weight: 2,
                    opacity: 0.8,
                  };
                }}
                onEachFeature={(feature: any, layer: any) => {
                  const name = feature?.properties?.name as string | undefined;
                  if (!name) return;

                  layer.on('click', () => {
                    const risk = getRiskLevelForCountry(name);
                    const employees = employeesByCountryKey.get(normalizeCountryKey(name)) || [];
                    const center = layer.getBounds?.()?.getCenter?.();
                    if (center) {
                      handleCountryClick({
                        name,
                        employees,
                        riskLevel: risk,
                        latitude: center.lat,
                        longitude: center.lng,
                      });
                    }
                  });
                }}
              />
            )}
            
            {/* Employee markers (countries with employees) */}
            {allCountryMarkers.map((country) => (
              <CircleMarker
                key={country.name}
                center={[country.latitude, country.longitude]}
                radius={Math.max(14, Math.min(30, 14 + country.employees.length * 4))}
                pathOptions={{
                  fillColor: getRiskColor(country.riskLevel),
                  fillOpacity: 0.85,
                  color: 'hsl(var(--background))',
                  weight: 3,
                }}
                eventHandlers={{
                  click: () => handleCountryClick(country),
                }}
              >
                <Popup>
                  <div className="p-1 min-w-[200px]">
                    <h3 className="font-semibold text-sm mb-1">{country.name}</h3>
                    <p className="text-xs text-muted-foreground mb-2">
                      Active travelers: {country.employees.length}
                    </p>
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-xs">Overall threat level:</span>
                      <Badge className={`${getRiskBadgeClass(country.riskLevel)} text-xs`}>
                        {country.riskLevel === 'high' ? 'High threat' : country.riskLevel === 'medium' ? 'Medium threat' : 'Low threat'}
                      </Badge>
                    </div>
                    <Button 
                      size="sm" 
                      variant="outline" 
                      className="w-full mt-2 text-xs"
                      onClick={() => handleCountryClick(country)}
                    >
                      View Details →
                    </Button>
                  </div>
                </Popup>
              </CircleMarker>
            ))}
          </MapContainer>
          
          {/* Threat Level Legend */}
          <div className="absolute bottom-4 right-4 bg-background rounded-lg shadow-lg p-3 z-[1000]">
            <h4 className="text-xs font-semibold mb-2">Threat levels</h4>
            <div className="space-y-1">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-success" />
                <span className="text-xs">Low threat</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-warning" />
                <span className="text-xs">Medium threat</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-destructive" />
                <span className="text-xs">High threat</span>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* High Priority Safety Alert History */}
      <Card>
        <CardHeader className="pb-4">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <CardTitle className="text-lg font-semibold">
              High priority safety alert history
            </CardTitle>
            <div className="flex items-center gap-2">
              <Tabs value={alertFilter} onValueChange={(v) => setAlertFilter(v as 'all' | 'active')} className="w-auto">
                <TabsList className="h-8">
                  <TabsTrigger value="all" className="text-xs px-3">All</TabsTrigger>
                  <TabsTrigger value="active" className="text-xs px-3">
                    Active alert
                    {sosAlerts.filter(a => a.is_active).length > 0 && (
                      <Badge variant="secondary" className="ml-1 h-4 px-1 text-[10px]">
                        {sosAlerts.filter(a => a.is_active).length}
                      </Badge>
                    )}
                  </TabsTrigger>
                </TabsList>
              </Tabs>
              <div className="relative">
                <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search..."
                  value={alertSearch}
                  onChange={(e) => setAlertSearch(e.target.value)}
                  className="pl-8 h-8 w-40 text-xs"
                />
              </div>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {filteredAlerts.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <AlertTriangle className="h-8 w-8 mx-auto mb-2 opacity-50" />
              <p>No safety alerts found</p>
            </div>
          ) : (
            <div className="space-y-4">
              {filteredAlerts.map((alert) => (
                <div key={alert.id} className="border rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <div className="flex items-center gap-2 min-w-[150px]">
                      <Flag className="h-4 w-4" />
                      <span className="font-medium text-sm">{alert.country_name}</span>
                      <Badge variant="outline" className="text-xs">
                        {alert.alert_type}
                      </Badge>
                    </div>
                  </div>
                  <div className="mt-3 flex items-start gap-3">
                    <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center text-lg">
                      {getAlertTypeIcon(alert.alert_type)}
                    </div>
                    <div className="flex-1">
                      <h4 className="font-medium text-sm">{alert.title}</h4>
                      <p className="text-xs text-muted-foreground mt-1">
                        Posted {formatDistanceToNow(new Date(alert.created_at), { addSuffix: true })}
                      </p>
                      {alert.description && (
                        <p className="text-sm text-muted-foreground mt-2 line-clamp-2">
                          {alert.description}
                        </p>
                      )}
                    </div>
                    <Button variant="link" size="sm" className="text-primary text-xs shrink-0">
                      See affected employees
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Country Details Sheet */}
      <Sheet open={!!selectedCountry} onOpenChange={() => handleCloseDetails()}>
        <SheetContent className="sm:max-w-md overflow-y-auto">
          {selectedCountry && (
            <>
              <SheetHeader className="border-b pb-4">
                <div className="flex items-center justify-between">
                  <SheetTitle className="text-lg">{selectedCountry.name}</SheetTitle>
                  <button onClick={handleCloseDetails} className="text-muted-foreground hover:text-foreground">×</button>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">Active travelers:</span>
                  <span className="font-medium">{selectedCountry.employees.length}</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">Overall threat level:</span>
                  <Badge className={`${getRiskBadgeClass(selectedCountry.riskLevel)}`}>
                    {selectedCountry.riskLevel === 'high' ? 'High threat' : selectedCountry.riskLevel === 'medium' ? 'Medium threat' : 'Low threat'}
                  </Badge>
                </div>
              </SheetHeader>
              
              <div className="mt-6">
                <h4 className="text-sm font-semibold mb-4">Current employees</h4>
                <div className="space-y-4">
                  {selectedCountry.employees.map((employee) => (
                    <div
                      key={employee.id}
                      className="p-4 rounded-lg border bg-card"
                    >
                      <div className="flex items-center gap-3 mb-3">
                        <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-primary font-semibold text-sm">
                          {employee.employeeName.charAt(0)}
                        </div>
                        <span className="font-medium">{employee.employeeName}</span>
                        {employee.isOnline && (
                          <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200 text-xs ml-auto">
                            <Radio className="h-3 w-3 mr-1 animate-pulse" />
                            Online
                          </Badge>
                        )}
                      </div>
                      <div className="grid grid-cols-2 gap-2 text-sm">
                        <div>
                          <span className="text-muted-foreground">Request ID:</span>
                        </div>
                        <div className="text-right font-mono text-xs">
                          W-{employee.requestId}
                        </div>
                        <div>
                          <span className="text-muted-foreground">Origin:</span>
                        </div>
                        <div className="text-right">
                          {employee.origin}
                        </div>
                        <div>
                          <span className="text-muted-foreground">Start date:</span>
                        </div>
                        <div className="text-right">
                          {format(new Date(employee.startDate), 'yyyy-MM-dd')}
                        </div>
                        <div>
                          <span className="text-muted-foreground">End date:</span>
                        </div>
                        <div className="text-right">
                          {format(new Date(employee.endDate), 'yyyy-MM-dd')}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
};

export default WorldMapTab;
